import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/apiSetup';
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Bell,
  Calendar,
  CheckCircle,
  CheckSquare,
  Clock,
  CreditCard,
  Eye,
  FileSearch,
  FileText,
  Home,
  IndianRupee,
  Loader2,
  LogOut,
  RefreshCw,
  Settings,
  ShieldCheck,
  User,
  UserCheck,
  Users,
  Wallet,
  XCircle
} from 'lucide-react';
import AttendanceManagement from '../admin/AttendanceManagement';
import EmployeeManagement from '../admin/EmployeeManagement';
import LoansAdvancesManagement from '../admin/LoansAdvancesManagement';
import Reports from '../admin/Reports';
import LeaveManagement from '../admin/LeaveManagement';
import ShiftRoster from './ShiftRoster';
import { PayrollManagement } from '../payroll';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const asArray = (value, key) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value[key])) return value[key];
  return [];
};

const normalizePayrollRuns = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.runs)) return value.runs;
  return [];
};

const extractError = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail) && data.detail.length > 0) return data.detail[0].msg || fallback;
  } catch {
    return fallback;
  }
  return fallback;
};

const badgeClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['approved', 'active', 'checker_approved', 'hq_approved'].includes(normalized)) return 'bg-green-100 text-green-800 border-green-200';
  if (['rejected', 'failed'].includes(normalized)) return 'bg-red-100 text-red-800 border-red-200';
  if (['draft', 'pending'].includes(normalized)) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
};

const Section = ({ title, subtitle, icon: Icon, action, children }) => (
  <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-green-50 text-green-700">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const SummaryCard = ({ title, value, subtitle, icon: Icon, color, onClick }) => (
  <motion.button
    whileHover={{ y: -2 }}
    onClick={onClick}
    className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-left"
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </motion.button>
);

const ActionButton = ({ icon: Icon, label, onClick, tone = 'green', disabled }) => {
  const tones = {
    green: 'bg-green-600 hover:bg-green-700 text-white',
    red: 'bg-red-600 hover:bg-red-700 text-white',
    blue: 'bg-blue-600 hover:bg-blue-700 text-white',
    gray: 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700',
    yellow: 'bg-yellow-500 hover:bg-yellow-600 text-white'
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${tones[tone] || tones.green}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
};

const DataList = ({ items, emptyText, renderItem }) => (
  <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
    {items.length === 0 ? (
      <div className="p-6 text-center text-sm text-gray-500">{emptyText}</div>
    ) : (
      items.map(renderItem)
    )}
  </div>
);

const EnhancedCheckerDashboard = () => {
  const { user, logout, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const activeTab = searchParams.get('tab') || 'dashboard';

  const setActiveTab = (tabId, replace = false) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!tabId || tabId === 'dashboard') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabId);
    }
    setSearchParams(nextParams, { replace });
  };
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [kpis, setKpis] = useState(null);
  const [data, setData] = useState({
    attendancePending: [],
    attendanceSummary: {},
    attendanceRecords: [],
    payrollDashboard: {},
    payrollRuns: [],
    employees: [],
    loans: [],
    advances: [],
    leaves: [],
    adjustments: [],
    loanStats: {},
    shiftSwaps: [],
    overtimeRequests: []
  });

  const isChecker = useMemo(() => {
    const role = String(user?.role_name || user?.role_display_name || '').toLowerCase();
    return (
      role.includes('checker') ||
      role.includes('hr_manager') ||
      role.includes('finance') ||
      role.includes('dean') ||
      role.includes('owner') ||
      hasPermission?.('payroll:approve') ||
      hasPermission?.('loan:approve') ||
      hasPermission?.('attendance:approve:dept')
    );
  }, [user, hasPermission]);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchJson = useCallback(async (url, label) => {
    const response = await fetch(buildApiUrl(url), { headers });
    if (!response.ok) throw new Error(await extractError(response, `Unable to load ${label}.`));
    return response.json();
  }, [headers]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    const today = new Date().toISOString().slice(0, 10);

    const results = await Promise.allSettled([
      fetchJson('/api/v1/workflow/checker/kpis', 'checker KPIs'),
      fetchJson('/api/v1/attendance/pending-approval?page=1&size=100', 'attendance approvals'),
      fetchJson(`/api/v1/attendance/today/all?attendance_date=${today}`, 'attendance summary'),
      fetchJson('/api/v1/benfish-payroll/runs?limit=50', 'payroll runs'),
      fetchJson('/api/v1/benfish-employees/search?limit=500&offset=0', 'employees'),
      fetchJson('/api/v1/benfish-loans/applications?status=pending_checker&limit=500', 'loan approvals'),
      fetchJson('/api/v1/benfish-loans/advance-applications?status=pending_checker&limit=500', 'advance approvals'),
      Promise.all([
        fetchJson('/api/v1/leaves/applications?status=maker_approved&page=1&size=100', 'checker leave approvals'),
        fetchJson('/api/v1/leaves/applications?status=checker_approved&page=1&size=100', 'HQ leave approvals'),
      ]).then(([checkerLeaves, hqLeaves]) => {
        const seen = new Set();
        return [...checkerLeaves, ...hqLeaves].filter((item) => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
      }),
      fetchJson('/api/v1/loan-adjustments/pending', 'loan adjustment approvals'),
    ]);

    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length > 0) {
      const message = failed.map((r) => r.reason?.message).filter(Boolean).join(' | ');
      setError(message || 'Some checker dashboard data could not be loaded.');
    }

    if (results[0].status === 'fulfilled') setKpis(results[0].value);

    const attendancePending = results[1].status === 'fulfilled' ? asArray(results[1].value, 'attendances') : [];
    const attendanceToday = results[2].status === 'fulfilled' ? results[2].value : {};
    const adjustmentsData = results[8].status === 'fulfilled' ? results[8].value : {};

    setData({
      attendancePending,
      attendanceSummary: attendanceToday.summary || {},
      attendanceRecords: attendanceToday.records || [],
      payrollDashboard: {},
      payrollRuns: results[3].status === 'fulfilled' ? normalizePayrollRuns(results[3].value) : [],
      employees: results[4].status === 'fulfilled' ? asArray(results[4].value, 'employees') : [],
      loans: results[5].status === 'fulfilled' ? (Array.isArray(results[5].value) ? results[5].value : []) : [],
      advances: results[6].status === 'fulfilled' ? (Array.isArray(results[6].value) ? results[6].value : []) : [],
      leaves: results[7].status === 'fulfilled' ? (Array.isArray(results[7].value) ? results[7].value : []) : [],
      adjustments: asArray(adjustmentsData, 'pending_adjustments'),
      loanStats: {},
      shiftSwaps: [],
      overtimeRequests: [],
    });
    setLoading(false);
  }, [fetchJson]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const stats = useMemo(() => {
    const draftPayrolls = data.payrollRuns.filter((run) => run.status === 'draft');
    const attendanceExceptions = data.attendanceRecords.filter(
      (record) =>
        !record.check_in_time ||
        (record.check_in_time && !record.check_out_time) ||
        Number(record.total_hours || 0) > 12 ||
        Number(record.late_by_minutes || 0) > 30
    );
    return {
      draftPayrolls,
      attendanceExceptions,
      kpis: kpis || {},
    };
  }, [data, kpis]);

  const queueItems = useMemo(() => {
    const attendance = data.attendancePending.slice(0, 5).map(item => ({
      id: `attendance-${item.id}`,
      rawId: item.id,
      type: 'attendance',
      title: 'Attendance approval',
      employee: `${item.employee_name || 'Unknown'} (${item.employee_code || 'N/A'})`,
      meta: item.attendance_date,
      amount: null,
      status: item.approval_status || 'pending'
    }));
    const payroll = stats.draftPayrolls.slice(0, 5).map(item => ({
      id: `payroll-${item.id}`,
      rawId: item.id,
      type: 'payroll',
      title: `Payroll ${item.payroll_month}/${item.payroll_year}`,
      employee: `${item.total_employees || 0} employees`,
      meta: item.run_type || 'fulltime',
      amount: formatCurrency(item.total_net_pay),
      status: item.status
    }));
    const loans = data.loans.filter((item) => item.status === 'pending_checker').slice(0, 5).map(item => ({
      id: `loan-${item.id}`,
      rawId: item.id,
      type: 'loan',
      title: item.loan_type?.name || item.loan_type?.code || 'Loan application',
      employee: `${item.employee?.full_name || 'Unknown'} (${item.employee?.emp_code || 'N/A'})`,
      meta: item.application_date,
      amount: formatCurrency(item.principal_amount),
      status: item.status,
      workflowStatus: item.status,
    }));
    const advances = data.advances.filter((item) => item.status === 'pending_checker').slice(0, 5).map(item => ({
      id: `advance-${item.id}`,
      rawId: item.id,
      type: 'advance',
      title: item.advance_type || 'Advance application',
      employee: `${item.employee?.full_name || 'Unknown'} (${item.employee?.emp_code || 'N/A'})`,
      meta: item.application_date,
      amount: formatCurrency(item.advance_amount),
      status: item.status,
      workflowStatus: item.status,
    }));
    const leaves = data.leaves.slice(0, 5).map(item => ({
      id: `leave-${item.id}`,
      rawId: item.id,
      type: 'leave',
      title: item.leave_type_name || 'Leave request',
      employee: `${item.employee_name || 'Unknown'} (${item.employee_code || 'N/A'})`,
      meta: `${item.from_date} to ${item.to_date}`,
      amount: `${item.no_of_days || 0} day(s)`,
      status: item.status,
      workflowStatus: item.status,
      requiresHqApproval: Boolean(item.requires_hq_approval),
    }));
    return [...attendance, ...payroll, ...loans, ...advances, ...leaves].slice(0, 12);
  }, [data, stats.draftPayrolls]);

  const allTabs = [
    { id: 'dashboard', label: 'Approvals', icon: Home, requiredPermission: null },
    { id: 'attendance', label: 'Attendance Review', icon: Clock, requiredPermission: ['attendance:view:all', 'attendance:view:dept', 'attendance:approve:dept', 'attendance:approve:all'] },
    { id: 'leave', label: 'Leave', icon: Calendar, requiredPermission: ['leave:view:all', 'leave:view:dept', 'leave:approve:dept', 'leave:approve:all'] },
    { id: 'shifts', label: 'Shift & Roster', icon: Users, requiredPermission: ['shift:view:all', 'shift:view:dept', 'shift:view:own', 'shift:create', 'shift:edit', 'shift:delete', 'shift:assign'] },
    { id: 'employees', label: 'Employee Review', icon: Users, requiredPermission: ['employee:view:all', 'employee:view:dept', 'employee:create', 'employee:update:all', 'employee:update:dept'] },
    { id: 'loans', label: 'Loan Approvals', icon: CreditCard, requiredPermission: ['loan:view:all', 'loan:view:dept', 'loan:approve:all'] },
    { id: 'payroll', label: 'Payroll Control', icon: IndianRupee, requiredPermission: ['payroll:view', 'payroll:generate', 'payroll:edit', 'payroll:approve', 'payroll:master:view', 'payroll:master:edit'] },
    { id: 'reports', label: 'Reports', icon: FileText, requiredPermission: ['report:view:all', 'report:view:dept', 'report:export'] },
  ];

  const tabs = allTabs.filter(tab => {
    if (tab.id === 'dashboard') return true;
    if (Array.isArray(tab.requiredPermission)) {
      return tab.requiredPermission.some(perm => hasPermission(perm));
    }
    return hasPermission(tab.requiredPermission);
  });

  useEffect(() => {
    if (tabs.length > 0 && activeTab !== 'dashboard' && !tabs.some(t => t.id === activeTab)) {
      setActiveTab('dashboard', true);
    }
  }, [tabs, activeTab]);

  const runAction = async (item, action) => {
    const reason = action === 'reject' ? window.prompt('Enter rejection reason:') : 'Approved from checker dashboard';
    if (action === 'reject' && (!reason || reason.trim().length < 10)) {
      setError('Rejection reason must be at least 10 characters.');
      return;
    }

    setActionLoading(`${item.id}-${action}`);
    setError('');
    setSuccess('');
    try {
  let url = '';
  let method = 'POST';
  let body = {};

      const actionId = Number(item?.rawId);
      if (!actionId || Number.isNaN(actionId)) {
        throw new Error('Invalid approval item. Refresh the dashboard and try again.');
      }

      if (item.type === 'attendance') {
        url = `/api/v1/attendance/${actionId}/${action}`;
        body = action === 'approve' ? { remarks: 'Approved from checker dashboard' } : { rejection_reason: reason };
      } else if (item.type === 'loan') {
        if ((item.workflowStatus || item.status) !== 'pending_checker') {
          throw new Error('Loan is not awaiting checker approval.');
        }
        url = `/api/v1/benfish-loans/applications/${actionId}/${action}`;
        body = { remarks: reason || 'Approved from checker dashboard' };
      } else if (item.type === 'advance') {
        if ((item.workflowStatus || item.status) !== 'pending_checker') {
          throw new Error('Advance is not awaiting checker approval.');
        }
        url = `/api/v1/benfish-loans/advance-applications/${actionId}/${action}`;
        body = { remarks: reason || 'Approved from checker dashboard' };
      } else if (item.type === 'leave') {
        const leaveStatus = item.workflowStatus || item.status;
        if (action === 'approve') {
          if (leaveStatus === 'maker_approved') {
            url = `/api/v1/leaves/applications/${actionId}/approve`;
          } else if (leaveStatus === 'checker_approved') {
            url = `/api/v1/leaves/applications/${actionId}/hq-approve`;
          } else {
            throw new Error(`Leave application cannot be approved while status is ${leaveStatus || 'unknown'}.`);
          }
        } else {
          url = `/api/v1/leaves/applications/${actionId}/reject`;
        }
        body = action === 'approve'
          ? { remarks: reason || 'Approved from checker dashboard', requires_hq_approval: Boolean(item.requiresHqApproval) }
          : { rejection_reason: reason };
      } else if (item.type === 'payroll' && action === 'approve') {
        url = `/api/v1/benfish-payroll/runs/${actionId}/approve`;
        method = 'PUT';
        body = null;
      } else {
        setActiveTab(item.type === 'payroll' ? 'payroll' : item.type === 'attendance' ? 'attendance' : 'loans');
        return;
      }

      const response = await fetch(buildApiUrl(url), {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined
      });
      if (!response.ok) throw new Error(await extractError(response, `Unable to ${action} ${item.type}.`));
      setSuccess(`${item.title} ${action === 'approve' ? 'approved' : 'rejected'} successfully.`);
      await loadDashboard();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {(error || success) && (
        <div className={`p-4 rounded-lg border flex items-center justify-between ${error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          <div className="flex items-center">
            {error ? <AlertCircle className="w-5 h-5 mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
            {error || success}
          </div>
          <button onClick={() => { setError(''); setSuccess(''); }} className="text-sm font-medium">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <SummaryCard title="Pending Approvals" value={stats.kpis.pending_approvals ?? 0} icon={CheckSquare} color="bg-green-600" onClick={() => setActiveTab('dashboard')} />
        <SummaryCard title="Attendance Exceptions" value={stats.kpis.attendance_exceptions ?? stats.attendanceExceptions.length} icon={AlertTriangle} color="bg-orange-600" onClick={() => setActiveTab('attendance')} />
        <SummaryCard title="Loan Approvals" value={stats.kpis.pending_loans ?? data.loans.length} icon={CreditCard} color="bg-indigo-600" onClick={() => setActiveTab('loans')} />
        <SummaryCard title="Advance Approvals" value={stats.kpis.pending_advances ?? data.advances.length} icon={Wallet} color="bg-blue-600" onClick={() => setActiveTab('advances')} />
        <SummaryCard title="Payroll Approvals" value={stats.kpis.pending_payroll ?? stats.draftPayrolls.length} icon={IndianRupee} color="bg-purple-600" onClick={() => setActiveTab('payroll')} />
        <SummaryCard title="Compliance Alerts" value={stats.kpis.compliance_alerts ?? data.adjustments.length} icon={ShieldCheck} color="bg-gray-700" onClick={() => setActiveTab('reports')} />
      </div>

      <Section
        title="Approval Queue"
        subtitle="Review pending attendance, payroll, onboarding, salary, loan, and leave items from one queue."
        icon={CheckSquare}
        action={<ActionButton icon={RefreshCw} label="Refresh" tone="gray" onClick={loadDashboard} />}
      >
        <DataList
          items={queueItems}
          emptyText="No pending approvals found."
          renderItem={(item) => (
            <div key={item.id} className="p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-gray-900">{item.title}</p>
                  <span className={`px-2 py-1 rounded-full border text-xs font-medium ${badgeClass(item.status)}`}>{item.status}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{item.employee}</p>
                <p className="text-xs text-gray-500 mt-1">{item.meta} {item.amount ? `- ${item.amount}` : ''}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <ActionButton icon={Eye} label="View Details" tone="gray" onClick={() => setActiveTab(item.type === 'attendance' ? 'attendance' : item.type === 'leave' ? 'leave' : item.type === 'payroll' ? 'payroll' : item.type === 'advance' ? 'advances' : item.type === 'loan' ? 'loans' : 'dashboard')} />
                {((item.type !== 'loan' && item.type !== 'advance') || item.status === 'pending_checker')
                  && (item.type !== 'leave' || ['maker_approved', 'checker_approved'].includes(item.status)) && (
                  <>
                    <ActionButton icon={CheckCircle} label="Approve" tone="green" onClick={() => runAction(item, 'approve')} disabled={actionLoading === `${item.id}-approve`} />
                    <ActionButton icon={XCircle} label="Reject" tone="red" onClick={() => runAction(item, 'reject')} disabled={item.type === 'payroll' || actionLoading === `${item.id}-reject`} />
                  </>
                )}
              </div>
            </div>
          )}
        />
      </Section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Section title="Payroll Control" subtitle="Approve draft payroll runs and open the payroll module for review." icon={IndianRupee}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ActionButton icon={Eye} label="Review Payroll" tone="gray" onClick={() => setActiveTab('payroll')} />
            {/* <ActionButton icon={ShieldCheck} label="Freeze Payroll" tone="blue" onClick={() => setActiveTab('payroll')} /> */}
            <ActionButton icon={CheckCircle} label="Release Payroll" tone="green" onClick={() => setActiveTab('payroll')} />
            <ActionButton icon={FileText} label="View Reports" tone="gray" onClick={() => setActiveTab('reports')} />
          </div>
        </Section>

        <Section title="Attendance Review" subtitle="Investigate anomalies, edits, missing attendance, and suspicious records." icon={Clock}>
          <DataList
            items={stats.attendanceExceptions.slice(0, 6)}
            emptyText="No attendance exceptions found today."
            renderItem={(record) => (
              <div key={record.id} className="p-3 flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">{record.employee_name}</p>
                  <p className="text-xs text-gray-500">{record.employee_code} - {record.attendance_date}</p>
                </div>
                <span className="text-xs font-medium text-orange-800 bg-orange-50 px-2 py-1 rounded-full">
                  {!record.check_in_time ? 'Missing attendance' : !record.check_out_time ? 'Missing checkout' : Number(record.total_hours || 0) > 12 ? 'High overtime' : 'Late'}
                </span>
              </div>
            )}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <ActionButton icon={CheckCircle} label="Approve Attendance" onClick={() => setActiveTab('attendance')} />
            <ActionButton icon={XCircle} label="Reject Attendance" tone="red" onClick={() => setActiveTab('attendance')} />
            <ActionButton icon={Users} label="Employee Attendance" tone="gray" onClick={() => setActiveTab('attendance')} />
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Section title="Compliance & Audit" subtitle="Manual overrides, payroll changes, and loan recovery changes." icon={FileSearch}>
          <DataList
            items={data.adjustments.slice(0, 5)}
            emptyText="No pending loan or advance adjustment audit records."
            renderItem={(item, index) => (
              <div key={`${item.adjustment_number || item.id}-${index}`} className="p-3">
                <p className="font-medium text-gray-900">{item.adjustment_number || item.adjustment_type || 'Adjustment'}</p>
                <p className="text-xs text-gray-500">Changed by {item.requested_by || 'Unknown'} - {item.requested_at ? new Date(item.requested_at).toLocaleString() : 'Pending'}</p>
                <p className="text-xs text-gray-600 mt-1">Amount adjusted: {formatCurrency(item.amount_adjusted)}</p>
              </div>
            )}
          />
        </Section>

        <Section title="Reports & Analytics" subtitle="Payroll, tax, PF, attendance, department, and synopsis reports." icon={BarChart3}>
          <div className="grid grid-cols-1 gap-3">
            {['Payroll Reports', 'Tax Reports', 'PF Reports', 'Attendance Reports', 'Department Salary Reports', 'Synopsis Report'].map(label => (
              <button key={label} onClick={() => setActiveTab(label === 'Attendance Reports' ? 'attendance' : 'reports')} className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between">
                {label}
                <FileText className="w-4 h-4 text-gray-400" />
              </button>
            ))}
          </div>
        </Section>

        <Section title="Compliance Alerts" subtitle="Pending loan/advance adjustments requiring checker attention." icon={AlertTriangle}>
          <DataList
            items={data.adjustments.slice(0, 5)}
            emptyText="No pending compliance adjustments."
            renderItem={(item, index) => (
              <div key={`${item.adjustment_number || item.id}-${index}`} className="p-3">
                <p className="font-medium text-gray-900">{item.adjustment_number || item.adjustment_type || 'Adjustment'}</p>
                <p className="text-xs text-gray-500">Requested by {item.requested_by || 'Unknown'}</p>
              </div>
            )}
          />
        </Section>
      </div>
    </div>
  );

  const renderContent = () => {
    const isTabAllowed = tabs.some(tab => tab.id === activeTab);
    if (!isTabAllowed && activeTab !== 'dashboard') {
      return (
        <div className="p-8 text-center bg-white rounded-lg shadow-sm border border-red-100 max-w-lg mx-auto my-12">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have the required permissions to access this section.</p>
        </div>
      );
    }

    if (activeTab === 'attendance') return <AttendanceManagement workflowMode="checker" />;
    if (activeTab === 'leave') return <LeaveManagement workflowMode="checker" />;
    if (activeTab === 'shifts') return <ShiftRoster workflowMode="checker" />;
    if (activeTab === 'employees') return <EmployeeManagement />;
    if (activeTab === 'loans' || activeTab === 'advances') return <LoansAdvancesManagement workflowMode="checker" />;
    if (activeTab === 'payroll') return <PayrollManagement />;
    if (activeTab === 'reports') return <Reports reportScope="checker" />;
    if (loading) {
      return (
        <div className="h-64 flex items-center justify-center text-gray-500">
          <Loader2 className="w-6 h-6 mr-2 animate-spin" />
          Loading checker dashboard
        </div>
      );
    }
    return renderDashboard();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center min-w-0">
              <CheckSquare className="w-8 h-8 text-green-600 mr-3 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">Checker Approval Dashboard</h1>
                <p className="text-sm text-gray-600 truncate">Approve, reject, audit, and control payroll compliance</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isChecker && (
                <span className="hidden md:inline-flex px-2 py-1 rounded-full bg-yellow-50 text-yellow-800 text-xs font-medium border border-yellow-200">
                  Limited checker permissions
                </span>
              )}
              <button onClick={loadDashboard} className="p-2 text-gray-400 hover:text-gray-600" title="Refresh dashboard">
                <RefreshCw className="w-5 h-5" />
              </button>
              {/* <button className="p-2 text-gray-400 hover:text-gray-600 relative">
                <Bell className="w-5 h-5" />
                {queueItems.length > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-4 h-4 px-1 flex items-center justify-center">{queueItems.length}</span>}
              </button> */}
              {/* <button className="p-2 text-gray-400 hover:text-gray-600">
                <Settings className="w-5 h-5" />
              </button> */}
              <div className="relative">
                <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-2 p-2 text-gray-700 hover:text-gray-900">
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">{user?.employee_name || 'Checker'}</span>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.employee_name || user?.username}</p>
                      <p className="text-xs text-gray-500">{user?.role_display_name || user?.role_name}</p>
                    </div>
                    <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-50">
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-gray-200 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6 min-w-max">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 border-b-2 text-sm font-medium ${
                  activeTab === tab.id ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {renderContent()}
      </main>
    </div>
  );
};

export default EnhancedCheckerDashboard;
